// localStorage-backed theme hook with system-preference fallback.
//
// Returns [theme, setTheme] where theme is 'light' or 'dark'. Persists to
// localStorage under 'cockpit-theme'. On every change, toggles the `dark`
// class on <html> so Tailwind's class-based dark mode strategy picks it up.

import { useEffect, useState } from 'react';

const KEY = 'cockpit-theme';

export type Theme = 'light' | 'dark';

function readInitial(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Ignore storage errors.
  }
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    // Ignore matchMedia errors.
  }
  return 'light';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(readInitial);

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(KEY, theme);
    } catch {
      // Ignore storage errors (private mode, quota, etc.).
    }
  }, [theme]);

  return [theme, setTheme];
}
